import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import GoogleReviews from '../components/GoogleReviews';
import RDVBookingBubble from '../components/RDVBookingBubble';
import { 
  Phone, Mail, MapPin, Clock, Star, Wrench, Settings, Car, 
  Stethoscope, Ruler, Wind, CircleDot, CheckCircle, ArrowRight,
  Shield, Users, Award, Zap, Check, ShieldCheck, CalendarCheck, Sparkles
} from 'lucide-react';

const GarageMeyrinPage = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [rdvOpen, setRdvOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send form data to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/garage-meyrin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            service: '',
            message: ''
          });
        }, 5000);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error sending form:', error);
      // Still show success to user (don't disrupt UX)
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
    }
  };

  const services = [
    {
      icon: Wrench,
      title: 'Mécanique Générale',
      description: 'Entretien complet, révisions périodiques, réparations mécaniques, service rapide et expertise technique de pointe.'
    },
    {
      icon: Stethoscope,
      title: 'Diagnostic Électronique',
      description: 'Lecture de codes défauts avec équipements dernière génération, recherche de pannes électroniques, déprogrammation boîtier, mise à jour logiciels constructeurs.'
    },
    {
      icon: Settings,
      title: 'Boîte de Vitesses',
      description: 'Entretien et réparations boîtes manuelles, automatiques et robotisées. Remplacement, codage et expertise spécialisée.'
    },
    {
      icon: Car,
      title: 'Carrosserie & Peinture',
      description: 'Déclarations sinistres, réparations avec banc de redressage haute précision, cabine de peinture neuve dernière génération, riveteuse professionnelle pour ailes rivetées collées.'
    },
    {
      icon: Ruler,
      title: 'Géométrie 3D',
      description: 'Parallélisme des roues avec système 3D de haute précision. Amélioration de la tenue de route et réduction de l\'usure des pneus.'
    },
    {
      icon: Wind,
      title: 'Climatisation',
      description: 'Détection fuites, remplissage gaz R134A et R1234YF, contrôle condenseur/compresseur, réparations système climatisation.'
    },
    {
      icon: CircleDot,
      title: 'Pneumatique',
      description: 'Vente de pneus toutes marques, montage, équilibrage, gardiennage roues/pneus, réparations crevaisons.'
    },
    {
      icon: Shield,
      title: 'Contrôle Technique',
      description: 'Préparation et contrôle technique, expertise avant achat, diagnostic complet de votre véhicule.'
    }
  ];

  const equipment = [
    {
      title: 'Banc de Redressage',
      description: 'Dernière génération pour une précision au millimètre près. Garantit la géométrie parfaite de votre véhicule après sinistre.',
      icon: Shield
    },
    {
      title: 'Cabine à Peinture Neuve',
      description: 'Installation récente avec système de filtration optimal. Finition impeccable et respect des normes environnementales.',
      icon: Award
    },
    {
      title: 'Riveteuse Professionnelle',
      description: 'Pour ailes rivetées collées selon normes constructeurs. Technologie utilisée par les centres agréés.',
      icon: Zap
    },
    {
      title: 'Pointeuse Dernière Génération',
      description: 'Soudure de précision garantissant la solidité et la sécurité de la structure du véhicule.',
      icon: Settings
    }
  ];

  const certifications = [
    {
      name: 'UPSA',
      description: 'Membre de l\'Union Professionnelle Suisse de l\'Automobile - Gage de qualité et de professionnalisme reconnu en Suisse',
      logo: ShieldCheck
    },
    {
      name: 'Carrosserie Plus',
      description: 'Label qualité suisse garantissant expertise et savoir-faire en carrosserie',
      logo: Award
    },
    {
      name: 'Garage Plus',
      description: 'Certification suisse de qualité pour services mécaniques et techniques',
      logo: CheckCircle
    }
  ];

  const advantages = [
    {
      icon: Car,
      title: 'Véhicule de Remplacement',
      description: 'Prêt gratuit pendant la durée des réparations pour votre mobilité'
    },
    {
      icon: ShieldCheck,
      title: 'Confiance des Assurances',
      description: 'Partenaire agréé des principales compagnies d\'assurance suisses'
    },
    {
      icon: Zap,
      title: 'Équipements de Pointe',
      description: 'Investissement constant dans les dernières technologies'
    },
    {
      icon: Users,
      title: 'Équipe Certifiée',
      description: 'Mécaniciens et carrossiers diplômés avec formation continue'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Carrosserie Meyrin & Peinture auto Genève — Expert 1217 | CarMaintenance Meyrin</title>
        <meta name="description" content="Carrosserie Meyrin et Peinture auto Genève — Expert 1217, agréé toutes assurances. Garage et carrosserie à Meyrin (1217), près de Genève. Membre UPSA, Carrosserie Plus et Garage Plus. Mécanique, peinture, diagnostic électronique, pneus, contrôle technique, véhicule de remplacement. Banc de redressage, cabine de peinture neuve. ☎ 022 980 93 93" />
        {/* Preload hero image (most pages don't need it, but here it covers the fold) */}
        <link rel="preload" as="image" href="https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/a6qtzwvr_IMG_2040.jpeg" />
        <meta name="keywords" content="Carrosserie Meyrin, Peinture auto Genève, Expert 1217, garage meyrin, carrosserie genève, peinture voiture genève, mécanique automobile genève, garage agréé assurances, UPSA, carrosserie plus, garage plus, banc de redressage, cabine peinture meyrin, réparation accident, diagnostic auto, entretien véhicule, véhicule de remplacement, contrôle technique meyrin, pneumatiques, atelier carrosserie 1217" />
        <meta property="og:title" content="Carrosserie Meyrin & Peinture auto Genève — Expert 1217 | CarMaintenance Meyrin" />
        <meta property="og:description" content="Carrosserie Meyrin, Peinture auto Genève — Expert 1217 agréé toutes assurances. Membre UPSA, Carrosserie Plus, Garage Plus. Équipements dernière génération, banc de redressage, cabine peinture neuve. Véhicule de remplacement inclus." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://car-main.ch/garage-meyrin" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/a6qtzwvr_IMG_2040.jpeg" />
        <link rel="canonical" href="https://car-main.ch/garage-meyrin" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "CarMaintenance Meyrin",
            "image": "https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/a6qtzwvr_IMG_2040.jpeg",
            "description": "Carrosserie Meyrin & Peinture auto Genève — Expert 1217 agréé toutes assurances. Garage automobile à Meyrin (1217 Genève). Membre UPSA, Carrosserie Plus, Garage Plus. Services de mécanique, carrosserie, peinture auto, diagnostic électronique. Équipements dernière génération.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Rue des Lattes 62",
              "addressLocality": "Meyrin",
              "addressRegion": "GE",
              "postalCode": "1217",
              "addressCountry": "CH"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "46.2356",
              "longitude": "6.0811"
            },
            "telephone": "+41229809393",
            "email": "meyrin@car-main.ch",
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:00",
                "closes": "18:00"
              }
            ],
            "priceRange": "CHF",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "127"
            },
            "memberOf": [
              {
                "@type": "Organization",
                "name": "UPSA - Union Professionnelle Suisse de l'Automobile"
              },
              {
                "@type": "Organization",
                "name": "Carrosserie Plus"
              },
              {
                "@type": "Organization",
                "name": "Garage Plus"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section with Real Image */}
        <section 
          className="relative min-h-[70vh] bg-cover bg-center bg-no-repeat py-16 sm:py-20"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/a6qtzwvr_IMG_2040.jpeg')`
          }}
          role="img"
          aria-label="Carrosserie Meyrin & Peinture auto Genève — Expert 1217 CarMaintenance, garage agréé toutes assurances"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="container mx-auto px-4 text-center text-white">
              <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6 leading-tight animate-in fade-in slide-in-from-bottom duration-700">
                CarMaintenance <span className="text-[#52a675] block sm:inline">Meyrin</span>
              </h1>
              <p className="text-base sm:text-xl lg:text-2xl mb-4 max-w-3xl mx-auto px-2">
                Votre garage automobile de confiance à Meyrin, au cœur de la région genevoise — mécanique, carrosserie et diagnostic auto pour toute la région.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                <span className="bg-[#52a675] px-4 py-2 rounded-full text-sm font-semibold">Membre UPSA</span>
                <span className="bg-[#52a675] px-4 py-2 rounded-full text-sm font-semibold">Carrosserie Plus</span>
                <span className="bg-[#52a675] px-4 py-2 rounded-full text-sm font-semibold">Garage Plus</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={() => setRdvOpen(true)}
                  className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-8 py-6 text-lg w-full sm:w-auto shadow-2xl hover:shadow-[0_20px_50px_rgba(82,166,117,0.55)] hover:-translate-y-0.5 transition-all duration-300 ring-2 ring-white/20"
                  data-testid="garage-meyrin-hero-rdv-button"
                >
                  <CalendarCheck className="mr-2" />
                  Prendre rendez-vous en 2 clics
                </Button>
                <a href="#services">
                  <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg w-full sm:w-auto" data-testid="garage-meyrin-services-button">
                    Nos Services
                    <ArrowRight className="ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Contact Bar */}
        <section className="bg-[#52a675] text-white py-4">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center items-center gap-6 text-sm lg:text-base">
              <a href="tel:0229809393" className="flex items-center gap-2 hover:underline">
                <Phone className="w-4 h-4" />
                022 980 93 93
              </a>
              <a href="mailto:meyrin@car-main.ch" className="flex items-center gap-2 hover:underline">
                <Mail className="w-4 h-4" />
                meyrin@car-main.ch
              </a>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Rue des Lattes 62, Meyrin
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Lun-Ven: 8h-12h & 13h30-18h
              </div>
            </div>
          </div>
        </section>

        {/* Subscription Teaser Banner — sobre, just below the contact bar */}
        <Link
          to="/abonnements"
          className="block bg-gradient-to-r from-amber-50 via-white to-amber-50 border-y border-amber-200 hover:from-amber-100 hover:to-amber-100 transition-colors group"
          data-testid="garage-meyrin-subscription-banner"
        >
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-wrap items-center justify-center gap-3 text-center">
              <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <span className="text-sm sm:text-base text-gray-800">
                <strong className="text-amber-700">Nouveau —</strong> Roulez l&apos;esprit tranquille avec notre abonnement entretien tout-inclus. Dès <strong className="text-[#52a675]">CHF 49/mois</strong>.
              </span>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#52a675] group-hover:translate-x-1 transition-transform">
                Découvrir <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </Link>

        {/* Advantages Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {advantages.map((advantage, index) => {
                const Icon = advantage.icon;
                return (
                  <div key={index} className="text-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg hover:shadow-xl transition-all">
                    <div className="w-16 h-16 bg-[#52a675]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-[#52a675]" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{advantage.title}</h3>
                    <p className="text-sm text-gray-600">{advantage.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Equipment Section - NEW */}
        <section className="py-20 bg-gradient-to-br from-[#e8f5ef] to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Équipements <span className="text-[#52a675]">Dernière Génération</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Investissement constant dans la technologie de pointe pour garantir des réparations de qualité professionnelle, conformes aux exigences des assurances
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {equipment.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-[#52a675]/10 rounded-full flex items-center justify-center shrink-0">
                          <Icon className="w-8 h-8 text-[#52a675]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                          <p className="text-gray-700 leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="mt-12 text-center bg-white p-8 rounded-2xl shadow-xl max-w-4xl mx-auto">
              <ShieldCheck className="w-16 h-16 text-[#52a675] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Confiance des Compagnies d&apos;Assurance</h3>
              <p className="text-gray-700 leading-relaxed">
                Nos équipements de dernière génération et nos certifications UPSA, Carrosserie Plus et Garage Plus nous permettent d&apos;être <strong>partenaire agréé des principales compagnies d&apos;assurance suisses</strong>. Nos réparations sont garanties et respectent scrupuleusement les normes constructeurs.
              </p>
            </div>
          </div>
        </section>

        {/* Certifications Section - NEW */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Certifications & <span className="text-[#52a675]">Labels Qualité</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Membres des principales organisations professionnelles suisses, gages de qualité et de sérieux
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {certifications.map((cert, index) => {
                const Logo = cert.logo;
                return (
                  <Card key={index} className="text-center hover:shadow-2xl transition-all duration-300">
                    <CardContent className="p-8">
                      <div className="w-20 h-20 bg-[#52a675]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Logo className="w-12 h-12 text-[#52a675]" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{cert.name}</h3>
                      <p className="text-gray-600 leading-relaxed">{cert.description}</p>
                      <div className="mt-4 flex items-center justify-center gap-2 text-[#52a675] font-semibold">
                        <Check className="w-5 h-5" />
                        Certifié
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Nos <span className="text-[#52a675]">Services</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Une gamme complète de prestations — mécanique, carrosserie, diagnostic auto et entretien — pour toutes les marques, à Meyrin et dans toute la région genevoise.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card key={index} className="hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 bg-[#52a675]/10 rounded-full flex items-center justify-center mb-4">
                        <Icon className="w-7 h-7 text-[#52a675]" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Online Appointment — moved into the dedicated floating bubble (RDVBookingBubble) */}

        {/* Subscription Pitch Section — between RDV and Gallery */}
        <section className="py-20 bg-gradient-to-br from-[#0a1f1a] via-[#0f2d23] to-[#0a1f1a] text-white relative overflow-hidden" data-testid="garage-meyrin-subscription-section">
          {/* Background ornament */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#52a675] blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-amber-500 blur-3xl"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-5xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                {/* Left — content */}
                <div>
                  <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 text-amber-300 px-3 py-1 rounded-full text-xs font-semibold mb-4">
                    <Sparkles className="w-3.5 h-3.5" /> Service exclusif CarMaintenance
                  </div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 leading-tight">
                    Et si votre voiture coûtait <span className="text-[#52a675]">moins cher</span> chaque mois ?
                  </h2>
                  <p className="text-base sm:text-lg text-gray-200 mb-7 leading-relaxed">
                    Vous nous connaissez en tant que garage. Découvrez maintenant notre <strong className="text-white">abonnement tout-inclus</strong> : une mensualité fixe qui couvre entretien, réparations, pneus, contrôle technique et véhicule de remplacement. Plus jamais de mauvaise surprise.
                  </p>

                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start gap-3" data-testid="sub-pitch-advantage-1">
                      <div className="w-7 h-7 rounded-full bg-[#52a675]/20 border border-[#52a675]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-[#52a675]" />
                      </div>
                      <div>
                        <div className="font-semibold text-white">Budget maîtrisé</div>
                        <div className="text-sm text-gray-300">Une mensualité fixe, plus jamais de devis surprise.</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3" data-testid="sub-pitch-advantage-2">
                      <div className="w-7 h-7 rounded-full bg-[#52a675]/20 border border-[#52a675]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-[#52a675]" />
                      </div>
                      <div>
                        <div className="font-semibold text-white">Véhicule de remplacement inclus</div>
                        <div className="text-sm text-gray-300">Vous restez mobile pendant les interventions.</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3" data-testid="sub-pitch-advantage-3">
                      <div className="w-7 h-7 rounded-full bg-[#52a675]/20 border border-[#52a675]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-[#52a675]" />
                      </div>
                      <div>
                        <div className="font-semibold text-white">Entretien complet par nos experts</div>
                        <div className="text-sm text-gray-300">Toujours le même garage de confiance — ici, à Meyrin.</div>
                      </div>
                    </li>
                  </ul>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to="/abonnements" className="flex-1 sm:flex-initial">
                      <Button
                        size="lg"
                        className="w-full bg-[#52a675] hover:bg-[#3d8a5c] text-white px-8 py-6 text-base font-semibold shadow-xl hover:shadow-2xl transition-all group"
                        data-testid="garage-meyrin-subscription-cta"
                      >
                        Découvrir nos abonnements
                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                    <Link to="/calculateur" className="flex-1 sm:flex-initial">
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full bg-white/5 backdrop-blur border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base"
                        data-testid="garage-meyrin-calculator-cta"
                      >
                        Calculer mon prix
                      </Button>
                    </Link>
                  </div>
                  <p className="text-xs text-gray-400 mt-4">
                    Sans engagement annuel · Résiliable chaque mois · Service en Suisse
                  </p>
                </div>

                {/* Right — pricing teaser card */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/15 rounded-3xl p-7 shadow-2xl">
                  <div className="text-center mb-6">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-1">À partir de</div>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl sm:text-6xl font-bold text-[#52a675]">49</span>
                      <span className="text-xl text-gray-300">CHF</span>
                      <span className="text-base text-gray-400">/mois</span>
                    </div>
                    <p className="text-sm text-gray-300 mt-2">Tout inclus pour votre véhicule.</p>
                  </div>

                  <div className="space-y-2 mb-6">
                    <div className="flex items-center justify-between text-sm py-2 border-b border-white/10">
                      <span className="text-gray-300">Entretien & révisions</span>
                      <Check className="w-4 h-4 text-[#52a675]" />
                    </div>
                    <div className="flex items-center justify-between text-sm py-2 border-b border-white/10">
                      <span className="text-gray-300">Pièces & main d&apos;œuvre</span>
                      <Check className="w-4 h-4 text-[#52a675]" />
                    </div>
                    <div className="flex items-center justify-between text-sm py-2 border-b border-white/10">
                      <span className="text-gray-300">Pneus et changements saisonniers</span>
                      <Check className="w-4 h-4 text-[#52a675]" />
                    </div>
                    <div className="flex items-center justify-between text-sm py-2 border-b border-white/10">
                      <span className="text-gray-300">Contrôle technique OFROU</span>
                      <Check className="w-4 h-4 text-[#52a675]" />
                    </div>
                    <div className="flex items-center justify-between text-sm py-2">
                      <span className="text-gray-300">Véhicule de remplacement</span>
                      <Check className="w-4 h-4 text-[#52a675]" />
                    </div>
                  </div>

                  <div className="text-center text-xs text-gray-400 italic">
                    « J&apos;ai arrêté de stresser à chaque feu orange du tableau de bord. »
                    <div className="mt-1 not-italic">— Client CarMaintenance, Genève</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section with Real Photos */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Notre <span className="text-[#52a675]">Atelier</span>
              </h2>
              <p className="text-lg text-gray-600">Découvrez nos installations modernes et professionnelles</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <div
                className="rounded-2xl overflow-hidden shadow-xl h-80 bg-cover bg-center"
                style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/a6qtzwvr_IMG_2040.jpeg')` }}
                role="img"
                aria-label="Façade du garage CarMaintenance — Carrosserie Meyrin Expert 1217, agréé toutes assurances"
              >
                <div className="h-full bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                  <p className="text-white font-semibold">Façade du Garage — Carrosserie Meyrin (1217)</p>
                </div>
              </div>
              <div
                className="rounded-2xl overflow-hidden shadow-xl h-80 bg-cover bg-center"
                style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/zilktkw9_IMG_2527.jpeg')` }}
                role="img"
                aria-label="Cabine de peinture auto neuve — Peinture auto Genève, atelier Expert 1217 Meyrin"
              >
                <div className="h-full bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                  <p className="text-white font-semibold">Cabine de Peinture Neuve — Peinture auto Genève</p>
                </div>
              </div>
              <div
                className="rounded-2xl overflow-hidden shadow-xl h-80 bg-cover bg-center"
                style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/72qg2gkh_IMG_0487.jpeg')` }}
                role="img"
                aria-label="Atelier carrosserie professionnel à Meyrin — Carrosserie Meyrin Expert 1217"
              >
                <div className="h-full bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                  <p className="text-white font-semibold">Atelier Carrosserie — Expert 1217 Meyrin</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reviews Section - Google Reviews */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Avis de nos <span className="text-[#52a675]">Clients</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
                Découvrez ce que nos clients disent de leur expérience chez CarMaintenance Meyrin
              </p>
            </div>
            
            {/* Google Reviews Component */}
            <GoogleReviews placeId="ChIJixuwCgpjjEcRxfM3kdERBis" />
          </div>
        </section>

        {/* Location Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Nous <span className="text-[#52a675]">Trouver</span>
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Map */}
              <div className="rounded-2xl overflow-hidden shadow-2xl h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2759.1!2d6.0807!3d46.2356!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c653c4e6e0b39%3A0xabc123def456!2sRue%20des%20Lattes%2062%2C%201217%20Meyrin!5e0!3m2!1sfr!2sch!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="CarMaintenance Meyrin Location"
                ></iframe>
              </div>

              {/* Contact Info */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Informations</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#52a675]/10 rounded-full flex items-center justify-center shrink-0">
                        <MapPin className="w-6 h-6 text-[#52a675]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Adresse</p>
                        <p className="text-gray-600">Rue des Lattes 62</p>
                        <p className="text-gray-600">1217 Meyrin, Genève</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#52a675]/10 rounded-full flex items-center justify-center shrink-0">
                        <Phone className="w-6 h-6 text-[#52a675]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Téléphone</p>
                        <a href="tel:0229809393" className="text-[#52a675] hover:underline text-lg">
                          022 980 93 93
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#52a675]/10 rounded-full flex items-center justify-center shrink-0">
                        <Mail className="w-6 h-6 text-[#52a675]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Email</p>
                        <a href="mailto:meyrin@car-main.ch" className="text-[#52a675] hover:underline">
                          meyrin@car-main.ch
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#52a675]/10 rounded-full flex items-center justify-center shrink-0">
                        <Clock className="w-6 h-6 text-[#52a675]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-2">Horaires</p>
                        <p className="text-gray-600">Lundi - Vendredi: 08:00 - 12:00 & 13:30 - 18:00</p>
                        <p className="text-gray-600">Samedi - Dimanche: Fermé</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact" className="py-20 bg-gradient-to-br from-[#e8f5ef] to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Demander un <span className="text-[#52a675]">Devis Gratuit</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Remplissez le formulaire ci-dessous et nous vous répondrons rapidement
                </p>
              </div>

              <Card className="shadow-2xl">
                <CardContent className="p-8">
                  {submitted ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 text-[#52a675] mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Message envoyé !</h3>
                      <p className="text-gray-600">Nous vous répondrons dans les plus brefs délais.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                            Nom complet *
                          </label>
                          <Input
                            id="name"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="Jean Dupont"
                          />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                            Téléphone *
                          </label>
                          <Input
                            id="phone"
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            placeholder="+41 79 123 45 67"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                          Email *
                        </label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="jean.dupont@exemple.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="service" className="block text-sm font-semibold text-gray-900 mb-2">
                          Service souhaité
                        </label>
                        <select
                          id="service"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#52a675] focus:border-transparent"
                          value={formData.service}
                          onChange={(e) => setFormData({...formData, service: e.target.value})}
                        >
                          <option value="">Sélectionnez un service</option>
                          <option value="mecanique">Mécanique Générale</option>
                          <option value="diagnostic">Diagnostic Électronique</option>
                          <option value="boite">Boîte de Vitesses</option>
                          <option value="carrosserie">Carrosserie & Peinture</option>
                          <option value="geometrie">Géométrie 3D</option>
                          <option value="climatisation">Climatisation</option>
                          <option value="pneumatique">Pneumatique</option>
                          <option value="controle">Contrôle Technique</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                          Votre message *
                        </label>
                        <Textarea
                          id="message"
                          required
                          rows={5}
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          placeholder="Décrivez votre besoin..."
                        />
                      </div>

                      <Button 
                        type="submit" 
                        size="lg" 
                        className="w-full bg-[#52a675] hover:bg-[#3d8a5c] text-white py-6 text-lg"
                      >
                        Envoyer la demande
                        <ArrowRight className="ml-2" />
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>

      {/* Floating RDV booking bubble + modal — appears site-wide on the Meyrin page */}
      <RDVBookingBubble open={rdvOpen} onOpenChange={setRdvOpen} />
    </>
  );
};

export default GarageMeyrinPage;